import { useClearableReducer } from "@client-shared/hooks/use-clearable-reducer";
import { ROUTE_PATHS } from "@client-shared/lib/constants";
import i18n from "@client-shared/lib/i18n";
import { toErrorMessage } from "@client-shared/lib/toErrorMessage";
import type { ShopifyAuthInput } from "@client-shared/services/api";
import * as api from "@client-shared/services/api";
import { prepareWooCommerceAuthorizeUrl, sanitizeWooCommerceDomain } from "@client-shared/utilities";
import { Installation, Service } from "@shared/types";
import * as React from "react";
import { toast } from "react-toastify";
import { installShopifyService, installWooCommerceService } from "../services";
import { useServicesState } from "../services/useServicesState";
import useVendorContext from "../vendor/useCurrentVendor";
import {
  deleteInstallation as deleteInstallationAction,
  initialState,
  putInstallation as putInstallationAction,
  reducer,
  resetInstallations,
  setError,
  setInitialized,
  setInstallations,
  setLoading,
} from "./actions";
import { clearShopifyOAuthState, readShopifyOAuthState, writeShopifyOAuthState } from "./shopifyOAuthStorage";
import type { CompleteShopifyOAuthFailureReason, InstallationResult } from "./types";
import {
  DeleteInstallationKeys,
  InstallationsActions,
  InstallationsContextState,
  PutInstallationKeys,
  UseInstallationUtils as UseInstallationMethods,
} from "./types";

export const defaultContextState: InstallationsContextState = [initialState, () => undefined];

export const InstallationsContext = React.createContext(defaultContextState);
type Props = {
  children: React.ReactNode;
};

export const InstallationsProvider: React.FC<Props> = ({ children }: Props) => {
  const [state, dispatch, resetState] = useClearableReducer(reducer, initialState);
  const [{ initialized: isVendorInitialized, currentVendor }] = useVendorContext();
  const [{ services }] = useServicesState();
  const previousVendorKeyRef = React.useRef(currentVendor?.vendorKey);
  // Bumped on logout and vendor changes so in-flight Shopify installs can tell their session has ended.
  const sessionGenerationRef = React.useRef(0);

  React.useEffect(() => {
    if (isVendorInitialized && !state.initialized) {
      dispatch(setInitialized(true));
    }
  }, [isVendorInitialized, state.initialized, dispatch]);

  // Reset installations whenever the vendor key changes (covers undefined→vendor,
  // vendor→undefined, and vendor1→vendor2 — e.g., admin impersonation switches)
  React.useEffect(() => {
    const currentVendorKey = currentVendor?.vendorKey;
    if (previousVendorKeyRef.current !== currentVendorKey) {
      sessionGenerationRef.current += 1;
      dispatch(resetInstallations());
    }
    previousVendorKeyRef.current = currentVendorKey;
  }, [currentVendor?.vendorKey, dispatch]);

  const clearState = React.useCallback(() => {
    sessionGenerationRef.current += 1;
    previousVendorKeyRef.current = undefined;
    clearShopifyOAuthState();
    resetState();
  }, [resetState]);

  const useInstallationMethods: UseInstallationMethods = React.useMemo(() => {
    return {
      clearState,
      fetch: () => fetchInstallations(services, dispatch),
      put: (installation: Installation, message: PutInstallationKeys) => putInstallation(installation, message, dispatch),
      delete: (installation: Installation, message: DeleteInstallationKeys) => deleteInstallation(installation, message, dispatch),
      getSingle: (installationId: string) => getSingleInstallation(installationId, dispatch),
      setError: (error: Error) => dispatch(setError(error)),
      installShopify: (params) => installShopify(params, services, dispatch, createSessionGuard(sessionGenerationRef)),
      installWooCommerce: (params) => installWooCommerce(params, createSessionGuard(sessionGenerationRef)),
      completeShopifyOAuth: (params) => completeShopifyOAuth(params, services, dispatch, createSessionGuard(sessionGenerationRef)),
    };
  }, [services, clearState, dispatch]);

  const contextValue: InstallationsContextState = React.useMemo(
    () => [state, dispatch, useInstallationMethods],
    [state, dispatch, useInstallationMethods]
  );

  return <InstallationsContext.Provider value={contextValue}>{children}</InstallationsContext.Provider>;
};

type SessionGuard = () => boolean;

// Captures the current session generation; the returned guard stays true only until logout or a vendor change.
const createSessionGuard = (generationRef: React.MutableRefObject<number>): SessionGuard => {
  const generation = generationRef.current;
  return () => generation === generationRef.current;
};

const sessionEndedError = () => new Error("Session ended before the installation completed");

// Side effects - defined outside the component
const fetchInstallations = async (services: Map<string, Service>, dispatch: React.Dispatch<InstallationsActions>) => {
  if (services.size === 0) {
    return;
  }
  dispatch(setLoading(true));
  try {
    const installations = await api.getInstallations();
    const filtered = installations.filter(
      (installation) =>
        installation.state !== "DELETED" &&
        installation.state !== "PENDING" &&
        services.get(installation.serviceId)?.serviceType === "remote"
    );
    dispatch(setInstallations(filtered));
    dispatch(setError(undefined));
    dispatch(setLoading(false));
    return filtered;
  } catch (e) {
    dispatch(
      setError({
        name: i18n.t("components.Installation.errors.getInstallations.name"),
        message: `${i18n.t("components.Installation.errors.getInstallations.message")} - ${toErrorMessage(e)}`,
      })
    );
    dispatch(setLoading(false));
  }
};

const getSingleInstallation = async (installationId: string, dispatch: React.Dispatch<InstallationsActions>) => {
  dispatch(setLoading(true));
  try {
    const installation = await api.getInstallation(installationId);
    if (installation.state === "DELETED" || installation.state === "PENDING") {
      dispatch(setLoading(false));
      dispatch(setError(new Error("Installation is not active")));
      return;
    }
    dispatch(setInstallations([installation]));
    dispatch(setError(undefined));
    dispatch(setLoading(false));
  } catch (e) {
    dispatch(setLoading(false));
    dispatch(setError(e as Error));
  }
};

const putInstallation = async (
  installation: Installation,
  message: PutInstallationKeys,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  dispatch(setLoading(true));
  try {
    const result = await api.putInstallation(installation);
    dispatch(setError(undefined));
    dispatch(putInstallationAction(result));
    toast.success(i18n.t(`components.Installation.put.${String(message)}`));
    dispatch(setLoading(false));
  } catch (e) {
    dispatch(setLoading(false));
    dispatch(setError(e as Error));
  }
};

const deleteInstallation = async (
  installation: Installation,
  message: DeleteInstallationKeys,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  dispatch(setLoading(true));
  try {
    const result = await api.deleteInstallation(installation);
    dispatch(deleteInstallationAction(result));
    toast.dark(i18n.t(`components.Installation.delete.${String(message)}`));
    dispatch(setError(undefined));
    dispatch(setLoading(false));
    return true;
  } catch (e) {
    dispatch(setLoading(false));
    dispatch(setError(e as Error));
    return false;
  }
};

const installShopify = async (
  params: {
    shop: string;
    selectedLocale?: string | null;
    vendorKey: string;
    installUrl?: string;
  } & ShopifyAuthInput,
  services: Map<string, Service>,
  dispatch: React.Dispatch<InstallationsActions>,
  isSessionActive: SessionGuard
) => {
  const { shop, selectedLocale } = params;

  if (!shop) {
    return { success: false, error: new Error("Shop is required") };
  }

  try {
    if (params.authMethod === "clientCredentials") {
      // Shopify's OAuth callback always returns the fully-qualified *.myshopify.com domain,
      // so store the shop in that canonical form to match it exactly in completeShopifyOAuth.
      const fqdnShop = shop.endsWith(".myshopify.com") ? shop : `${shop}.myshopify.com`;
      const {
        authorizationUrl,
        state: oauthState,
        encryptedCreds,
      } = await api.startShopifyAuthorize({
        shop: fqdnShop,
        clientId: params.clientId,
        clientSecret: params.clientSecret,
        locale: selectedLocale ?? undefined,
        targetHost: process.env.REACT_APP_CLIENT_HOST
          ? `https://${process.env.REACT_APP_CLIENT_HOST.replace(/^https?:\/\//, "")}`
          : undefined,
      });
      if (!isSessionActive()) {
        return { success: false, error: sessionEndedError() };
      }
      console.info("[shopify-oauth] starting authorize", { inputShop: shop, normalisedShop: fqdnShop, stateLength: oauthState.length });
      writeShopifyOAuthState({
        state: oauthState,
        encryptedCreds,
        shop: fqdnShop,
        locale: selectedLocale ?? undefined,
      });
      return { success: true, redirectUrl: authorizationUrl };
    }

    await installShopifyService({
      shop,
      locale: selectedLocale ?? undefined,
      authMethod: "accessToken",
      accessToken: params.accessToken,
    });
    if (!isSessionActive()) {
      return { success: false, error: sessionEndedError() };
    }
    await fetchInstallations(services, dispatch);
    if (!isSessionActive()) {
      return { success: false, error: sessionEndedError() };
    }
    const redirectUrl = `${ROUTE_PATHS.SETTINGS}?success=1&shop=${shop}`;
    return { success: true, redirectUrl };
  } catch (e) {
    return { success: false, error: e as Error };
  }
};

const completeShopifyOAuth = async (
  params: { code: string; state: string; shop: string },
  services: Map<string, Service>,
  dispatch: React.Dispatch<InstallationsActions>,
  isSessionActive: SessionGuard
): Promise<InstallationResult & { reason?: CompleteShopifyOAuthFailureReason }> => {
  const stored = readShopifyOAuthState();
  if (!stored) {
    return { success: false, error: new Error("Shopify OAuth session not found"), reason: "sessionExpired" };
  }

  // Defensive normalization: URLSearchParams.get() already decodes (%3A -> :, %2C -> ,),
  // so both sides should match unencoded. decodeSafe is a safety net in case a future
  // redirect double-encodes a value — it prevents a spurious mismatch, never masks a real one.
  const decodeSafe = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const storedState = decodeSafe(stored.state);
  const paramState = decodeSafe(params.state);
  const storedShop = decodeSafe(stored.shop);
  const paramShop = decodeSafe(params.shop);

  if (storedState !== paramState) {
    console.warn("[shopify-oauth] state mismatch", { storedStateLength: storedState.length, paramStateLength: paramState.length });
    clearShopifyOAuthState();
    return { success: false, error: new Error("Shopify OAuth state mismatch"), reason: "stateInvalid" };
  }
  if (storedShop !== paramShop) {
    console.warn("[shopify-oauth] shop mismatch", { storedShop, paramShop });
    clearShopifyOAuthState();
    return { success: false, error: new Error("Shopify OAuth shop mismatch"), reason: "shopInvalid" };
  }

  try {
    await installShopifyService({
      shop: params.shop,
      locale: stored.locale,
      authMethod: "oauthCompletion",
      code: params.code,
      state: params.state,
      encryptedCreds: stored.encryptedCreds,
    });
    if (!isSessionActive()) {
      return { success: false, error: sessionEndedError(), reason: "sessionExpired" };
    }
    clearShopifyOAuthState();
    await fetchInstallations(services, dispatch);
    if (!isSessionActive()) {
      return { success: false, error: sessionEndedError(), reason: "sessionExpired" };
    }
    return { success: true };
  } catch (e) {
    if (!isSessionActive()) {
      return { success: false, error: sessionEndedError(), reason: "sessionExpired" };
    }
    clearShopifyOAuthState();
    // installShopifyService here is the wrapped provider call, so by this point the caught
    // error has already been through buildInstallShopifyError — e.message is always one of its
    // final, translated strings (e.g. "Sorry, the following access scopes are missing: ..."),
    // never the raw backend text. So there's nothing left here to keyword-match against; the
    // reason stays the generic "backendError" marker, and the callback page displays e.message
    // directly instead of trying to re-derive a more specific reason from it.
    return { success: false, error: e instanceof Error ? e : new Error(String(e)), reason: "backendError" };
  }
};

const installWooCommerce = async (
  params: { shop: string; selectedLocale?: string | null; serviceName: string; returnPath?: string },
  isSessionActive: SessionGuard
) => {
  const { shop, selectedLocale, serviceName, returnPath } = params;

  if (!shop) {
    return { success: false, error: new Error("Shop domain is required") };
  }

  const clientHost = process.env.REACT_APP_CLIENT_HOST
    ? `https://${process.env.REACT_APP_CLIENT_HOST.replace(/https:\/\//, "")}`
    : undefined;
  const apiHost = process.env.REACT_APP_API_HOST ? `https://${process.env.REACT_APP_API_HOST.replace(/https:\/\//, "")}` : undefined;

  if (!apiHost || !clientHost) {
    return { success: false, error: new Error("API or Client host not configured") };
  }

  try {
    const domain = sanitizeWooCommerceDomain(shop);
    const jwtToken = await installWooCommerceService(domain, selectedLocale ?? undefined);

    if (!isSessionActive()) {
      return { success: false, error: sessionEndedError() };
    }

    if (!jwtToken) {
      return { success: false, error: new Error("Failed to get authorization token") };
    }

    const authorizeUrl = prepareWooCommerceAuthorizeUrl(domain, jwtToken, apiHost, clientHost, returnPath);

    if (!authorizeUrl) {
      return {
        success: false,
        error: new Error(i18n.t("components.ServiceForm.error.authorizeURL", { service: serviceName })),
      };
    }

    return { success: true, redirectUrl: authorizeUrl };
  } catch (e) {
    return { success: false, error: e as Error };
  }
};
